// @flow
import React, { Component, type Node } from 'react';
import classnames from 'classnames';
import shortId from 'shortid';
import { isString, noop } from 'lodash';

import { COLORS } from 'Components/utils/color/colors';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { ICONS } from 'Components/utils/icon/icons';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';

import { themes } from './InputSwitch.themes';

type Props = {
  ariaDescribedby?: string,
  ariaLabelledby?: string,
  className?: string,
  color?: Colors,
  dataTestId?: string,
  iconChecked?: IconName,
  iconUnChecked?: IconName,
  id?: string,
  isChecked?: boolean,
  isDisabled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  isSwitch?: boolean,
  label?: string | Node,
  labelClassName?: string,
  name?: string,
  onChange?: (name: string, checked: boolean, value: string | boolean) => void,
  onClick?: (SyntheticEvent<>) => void,
  size?: 'small' | 'medium' | 'large',
  theme?: string,
  validationState?: string,
  value?: string | boolean,
};

type State = {
  isChecked: boolean,
};

export class InputSwitch extends Component<Props, State> {
  static defaultProps = {
    color: COLORS.BLUE,
    iconChecked: ICONS.CHECK,
    iconUnChecked: null,
    isDisabled: false,
    isLoading: false,
    size: 'medium',
    theme: 'default',
    validationState: 'NOT_VALIDATED',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isChecked: props.isChecked || false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.isChecked !== this.props.isChecked) {
      this.setState({ isChecked: this.props.isChecked });
    }
  }

  onChange = () => {
    const {
      isDisabled,
      isLoading,
      isReadOnly,
      name = '',
      onChange = noop,
      value = '',
    } = this.props;

    if (!isReadOnly && !isDisabled && !isLoading) {
      const isChecked = !this.state.isChecked;

      this.setState(
        {
          isChecked,
        },
        () => onChange(name, isChecked, value),
      );
    }
  };

  getIcon(): IconName {
    const { iconChecked = null, iconUnChecked = null } = this.props;
    return this.state.isChecked ? iconChecked : iconUnChecked;
  }

  render() {
    const {
      ariaDescribedby,
      ariaLabelledby,
      className,
      id = `InputSwitch_${shortId.generate()}`,
      isDisabled,
      isLoading,
      isReadOnly,
      isRequired,
      isSwitch,
      label,
      labelClassName,
      name,
      onClick,
      theme,
      value,
      dataTestId,
    } = this.props;

    const { isChecked } = this.state;

    const {
      thChecked,
      thCheckedStyle,
      thLabel,
      thLoading,
      thRoot,
      thSwitch,
      thSwitchStyle,
      thSwitchToggler,
      thSwitchTogglerStyle,
    } = themes[theme]({ isChecked, ...this.props });

    return (
      <div data-testid={dataTestId} className={classnames(className, thRoot)}>
        {isSwitch ? (
          <button
            aria-checked={isChecked}
            aria-describedby={ariaDescribedby}
            aria-disabled={isDisabled}
            aria-labelledby={ariaLabelledby}
            className={thSwitch}
            data-testid={id}
            onClick={this.onChange}
            role="checkbox"
            style={thSwitchStyle}
            type="button"
          >
            <span className={thSwitchToggler} style={thSwitchTogglerStyle}>
              {isChecked ? <Icon name={ICONS.CHECK} /> : <Icon name={ICONS.CROSS} width="0.7rem" />}
            </span>
          </button>
        ) : (
          <button
            aria-checked={isChecked}
            aria-describedby={ariaDescribedby}
            aria-disabled={isDisabled}
            aria-labelledby={ariaLabelledby}
            className={classnames('reset-button', thChecked)}
            data-testid={id}
            disabled={isDisabled}
            onClick={this.onChange}
            role="checkbox"
            style={thCheckedStyle}
            type="button"
          >
            <Icon name={this.getIcon()} width="100%" />
          </button>
        )}
        <input
          checked={isChecked}
          className="hidden"
          disabled={isDisabled}
          id={id}
          label={label}
          name={name}
          onChange={this.onChange}
          onClick={onClick}
          required={isRequired}
          type="checkbox"
          value={value}
        />
        {isLoading &&
          !isDisabled &&
          !isReadOnly && (
            <div className={thLoading}>
              <Spinner size="small" />
            </div>
          )}
        {!!label && (
          <label className={classnames(labelClassName, thLabel)} htmlFor={id}>
            {isString(label) ? getFormattedLabel(label, isRequired) : label}
          </label>
        )}
      </div>
    );
  }
}
